import React, { useEffect, useState } from 'react';
import { supabase, checkUserPaymentStatus, cancelSubscription } from '../../lib/supabase';
import { Trophy, PlusCircle, UserPlus, ArrowLeft, CheckCircle2, Calendar, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../../lib/userStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { useLeagueSelection } from '../../lib/hooks/useLeagueSelection';
import { useEmbedStore } from '../../lib/embedStore';

interface League {
  league_id: number;
  name: string;
  league_settings?: {
    require_payment: boolean;
    league_fee: number;
  };
  paymentStatus?: {
    hasPaid: boolean;
    paymentData: any;
    isRecurring: boolean;
    subscriptionActive: boolean;
    nextPaymentDate?: string;
  };
}

export function SelectLeague() {
  const navigate = useNavigate();
  const { username, user_id } = useUserStore();
  const setSelectedLeague = useLeagueStore((state) => state.setSelectedLeague);
  const { language } = useLanguageStore();
  const { selectLeague, loading: selectingLeague } = useLeagueSelection();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const location = useLocation();

  // Debug UI states (only for non-embed)
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [lastError, setLastError] = useState<any>(null);
  const [lastRejection, setLastRejection] = useState<any>(null);
  const [lastSelectResult, setLastSelectResult] = useState<any>(null);
  const [debugJSON, setDebugJSON] = useState('');
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (!username || !user_id) {
      setLoading(false);
      return;
    }

    async function fetchUserLeagues() {
      try {
        // Récupérer les ligues où l'utilisateur est membre via leagues_users
        const { data: membershipData, error: membershipError } = await supabase
          .from('leagues_users')
          .select('league_id')
          .eq('user_id', user_id);

        if (membershipError && membershipError.code !== 'PGRST116') throw membershipError;

        if (!membershipData || membershipData.length === 0) {
          setLeagues([]);
          return;
        }

        // Récupérer les détails des ligues
        const leagueIds = membershipData.map(ul => ul.league_id);
        
        const { data: leaguesData, error: leaguesError } = await supabase
          .from('leagues')
          .select('league_id, name')
          .in('league_id', leagueIds);

        if (leaguesError) throw leaguesError;

        // Récupérer les paramètres de paiement pour ces ligues
        const { data: settingsData, error: settingsError } = await supabase
          .from('league_settings')
          .select('league_id, require_payment, league_fee')
          .in('league_id', leagueIds);

        if (settingsError && settingsError.code !== 'PGRST116') {
          console.warn('Erreur lors de la récupération des paramètres:', settingsError);
        }

        // Combiner les données
        const userLeaguesWithSettings = leaguesData?.map(league => ({
          league_id: league.league_id,
          name: league.name,
          league_settings: settingsData?.find(setting => setting.league_id === league.league_id)
        })) || [];

        // Récupérer le statut de paiement pour chaque ligue
        const leaguesWithPaymentStatus = await Promise.all(
          userLeaguesWithSettings.map(async (league) => {
            if (league.league_settings?.require_payment && user_id) {
              const paymentStatus = await checkUserPaymentStatus(league.league_id, parseInt(user_id));
              return {
                ...league,
                paymentStatus
              };
            }
            return league;
          })
        );

        setLeagues(leaguesWithPaymentStatus);
      } catch (error: any) {
        console.error('Error:', error);
        toast.error('Erreur lors du chargement de vos ligues');
      } finally {
        setLoading(false);
      }
    }

    fetchUserLeagues();
  }, [username, user_id]);

  useEffect(() => {
    try {
      const ua = navigator.userAgent || '';
      setIsIOS(/iPhone|iPad|iPod/i.test(ua));
    } catch (e) {
      setIsIOS(false);
    }

    const onError = (ev: ErrorEvent) => {
      setLastError({ message: ev.message, filename: ev.filename, lineno: ev.lineno, colno: ev.colno });
    };

    const onRejection = (ev: PromiseRejectionEvent) => {
      setLastRejection({ reason: (ev && (ev as any).reason) || String(ev) });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection as any);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection as any);
    };
  }, [username, user_id]);

  // Recompute debug JSON when pieces change
  useEffect(() => {
    if (isEmbed) return; // never expose in embed

    const params = new URLSearchParams(location.search);
    const forceDebug = params.get('debug') === '1' || params.get('debug') === 'true';
    if (!isIOS && !forceDebug) {
      setDebugJSON('');
      return;
    }

    const info = {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      width: typeof window !== 'undefined' ? window.innerWidth : null,
      height: typeof window !== 'undefined' ? window.innerHeight : null,
      devicePixelRatio: typeof window !== 'undefined' ? (window as any).devicePixelRatio : null,
      pathname: location.pathname,
      lastError,
      lastRejection,
      lastSelectResult,
    };

    try {
      setDebugJSON(JSON.stringify(info, null, 2));
    } catch (e) {
      setDebugJSON(String(info));
    }
  }, [isEmbed, isIOS, location.pathname, lastError, lastRejection, lastSelectResult, location.search]);

  const handleSelectLeague = async (league: League) => {
    const result = await selectLeague(league.league_id);
    setLastSelectResult(result);

    if (result && result.success) {
      setSelectedLeague({ id: league.league_id, name: league.name });
      navigate('/my-office');
    }
    // Les erreurs sont gérées dans le hook useLeagueSelection
  };

  const handleCancelSubscription = async (league: League) => {
    if (!user_id) return;

    const confirmCancel = window.confirm(
      'Êtes-vous sûr de vouloir annuler votre abonnement ? Vous conserverez l\'accès jusqu\'à la fin de la période payée.'
    );

    if (!confirmCancel) return;

    try {
      const result = await cancelSubscription(league.league_id, parseInt(user_id));
      
      if (result.success) {
        toast.success(result.message || 'Abonnement annulé avec succès');
        // Recharger la liste des ligues pour mettre à jour l'affichage
        window.location.reload();
      } else {
        toast.error(result.error?.message || 'Erreur lors de l\'annulation');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Erreur lors de l\'annulation de l\'abonnement');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  if (loading) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!username) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <Trophy className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">{translate('welcome.to.dynasty', language)}</h2>
          <p className="text-muted mb-4">{translate('start.journey', language)}</p>
          <div className="d-grid gap-2">
            <button 
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
              onClick={() => navigate('/create-league')}
            >
              <PlusCircle size={20} />
              {translate('create.league.button', language)}
            </button>
            <button 
              className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
              onClick={() => navigate('/join-league')}
            >
              <UserPlus size={20} />
              {translate('join.existing.league', language)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Trophy className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">{translate('my.leagues', language)}</h2>
        </div>

        {leagues.length === 0 ? (
          <div className="text-center">
            <p className="mb-4">{translate('no.leagues.yet', language)}</p>
            <div className="d-grid gap-2">
              <button 
                className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate('/create-league')}
              >
                <PlusCircle size={20} />
                {translate('create.league.button', language)}
              </button>
              <button 
                className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate('/join-league')}
              >
                <UserPlus size={20} />
                {translate('join.existing.league', language)}
              </button>
            </div>
          </div>
        ) : (
          <div className="d-grid gap-3">
            {leagues.map((league) => (
              <div
                key={league.league_id}
                className="card border border-2 hover:border-primary transition-colors"
              >
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-1">{league.name}</h5>
                      
                      {league.league_settings?.require_payment && (
                        <div className="mb-2 d-flex flex-column gap-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary">
                              💳 {formatPrice(league.league_settings.league_fee)} $ CAD requis
                            </span>
                            {league.paymentStatus?.hasPaid && (
                              <span className="badge bg-success d-flex align-items-center gap-1">
                                <CheckCircle2 size={14} />
                                Payé
                              </span>
                            )}
                          </div>
                          
                          {/* Informations sur l'abonnement récurrent */}
                          {league.paymentStatus?.isRecurring && league.paymentStatus?.hasPaid && (
                            <div className="d-flex flex-column gap-1">
                              {league.paymentStatus?.subscriptionActive ? (
                                <>
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-info d-flex align-items-center gap-1">
                                      <Calendar size={14} />
                                      Abonnement annuel actif
                                    </span>
                                    {/* Bouton d'annulation masqué */}
                                  </div>
                                  {league.paymentStatus?.nextPaymentDate && (
                                    <small className="text-muted">
                                      Prochain paiement: {formatDate(league.paymentStatus.nextPaymentDate)}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="badge bg-warning">
                                  Abonnement annulé - Accès jusqu'à expiration
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Message pour paiement unique */}
                          {!league.paymentStatus?.isRecurring && league.paymentStatus?.hasPaid && (
                            <small className="text-info">
                              💡 Paiement unique - Considérez l'abonnement annuel pour un renouvellement automatique
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleSelectLeague(league)}
                    disabled={loading}
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                  >
                    <Trophy size={20} />
                    {loading ? 'Sélection...' : 'Sélectionner cette ligue'}
                  </button>
                </div>
              </div>
            ))}
            
            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/join-league')}
                className="btn btn-success d-flex align-items-center justify-content-center gap-2 mx-auto"
              >
                <UserPlus size={20} />
                Rejoindre une nouvelle ligue
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Debug panel (non-embed only) */}
      {!isEmbed && (isIOS || new URLSearchParams(location.search).get('debug') === '1') && (
        <div style={{ position: 'fixed', right: 12, bottom: 12, zIndex: 2000 }}>
          <div style={{ width: 340, maxWidth: '92vw', background: '#0b1221', color: '#fff', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <strong>DEBUG iPhone</strong>
              <div>
                <button className="btn btn-sm btn-light me-1" onClick={() => { setIsDebugOpen(!isDebugOpen); }}>{isDebugOpen ? 'Fermer' : 'Ouvrir'}</button>
                <button className="btn btn-sm btn-outline-light" onClick={async () => { try { await navigator.clipboard.writeText(debugJSON || ''); toast.success('Copié dans le presse-papiers'); } catch { toast.error('Impossible de copier'); } }}>Copier</button>
              </div>
            </div>
            {isDebugOpen && (
              <div style={{ padding: 8 }}>
                <textarea readOnly value={debugJSON} style={{ width: '100%', height: 220, background: '#071022', color: '#e6eef8', border: 'none', padding: 8, borderRadius: 4, fontSize: 12 }} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}